import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { publicarEvento, getEventoInfo } from "@/features/evento/eventoThunks";
import { motion, AnimatePresence } from "framer-motion";
import Modal from "@/components/Modals/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export default function PublicarEvento({
  onClose,
  eventoUid = null,
  idEmpresa = null,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: paramFromUrl } = useParams();

  // Referencias
  const termContainerRef = useRef(null);
  const hasFetchedFreshRef = useRef(false);

  // Estados de UI
  const [hasReadTerms, setHasReadTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [publishSuccess, setPublishSuccess] = useState(false);

  // Estados de validación
  const [validations, setValidations] = useState([]);
  const [isValidating, setIsValidating] = useState(true);
  const [allValidationsPassed, setAllValidationsPassed] = useState(false);
  const [aforoStatus, setAforoStatus] = useState(null); // 'good', 'moderate', 'low', 'exceeded'
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Redux state
  const {
    isLoading: isLoadingEvento,
    error: errorEvento,
    infoEvento,
  } = useSelector((state) => state.evento);

  // Términos y condiciones para publicar evento
  const termsAndConditions = `
    TÉRMINOS Y CONDICIONES PARA PUBLICAR EVENTOS
    
    1. PUBLICACIÓN DE EVENTO
    Al publicar un evento en nuestra plataforma, usted confirma que toda la información proporcionada es veraz y completa. Una vez publicado, el evento estará disponible para el público y se podrán realizar ventas de tickets.
    
    2. RESPONSABILIDADES AL PUBLICAR
    2.1 Al publicar, usted acepta que el evento cumple con todos los requisitos legales.
    2.2 Debe tener todos los permisos y licencias necesarios para realizar el evento.
    2.3 La información del evento debe estar completa y actualizada.
    2.4 Es responsable de mantener actualizada la información del evento hasta su realización.
    
    3. VENTA DE TICKETS
    3.1 Una vez publicado, los usuarios podrán comprar tickets según la configuración establecida.
    3.2 Debe honrar todas las ventas realizadas a través de la plataforma.
    3.3 Los precios y condiciones no pueden modificarse arbitrariamente después de la publicación.
    3.4 Debe cumplir con las políticas de aforo establecidas.
    
    4. POLÍTICAS DE CANCELACIÓN POST-PUBLICACIÓN
    4.1 La cancelación de eventos publicados debe seguir procedimientos específicos.
    4.2 Debe notificar con suficiente anticipación en caso de cancelación.
    4.3 Aplican políticas de reembolso según los términos de venta.
    4.4 La plataforma se reserva el derecho de despublicar eventos que incumplan términos.
    
    5. COMPROMISOS DEL ORGANIZADOR
    5.1 Garantiza que el evento se realizará en la fecha, hora y lugar especificados.
    5.2 Proporcionará el acceso adecuado a todos los compradores de tickets.
    5.3 Mantendrá los estándares de calidad y seguridad prometidos.
    5.4 Atenderá consultas y reclamos de los asistentes de manera oportuna.
    
    6. MODIFICACIONES POST-PUBLICACIÓN
    6.1 Los cambios menores pueden realizarse previa notificación.
    6.2 Cambios significativos requieren aprobación y notificación a compradores.
    6.3 La plataforma debe ser notificada de cualquier cambio importante.
    6.4 Se reserva el derecho de despublicar eventos con cambios no autorizados.
    
    7. PROMOCIÓN Y MARKETING
    7.1 La plataforma puede promocionar su evento en sus canales oficiales.
    7.2 Debe proporcionar material promocional de calidad si es solicitado.
    7.3 No puede realizar promociones que contradigan los términos de la plataforma.
    7.4 Respetará las directrices de marketing de la plataforma.
    
    8. CUMPLIMIENTO LEGAL
    8.1 El evento debe cumplir con todas las regulaciones locales y nacionales.
    8.2 Debe contar con seguros y permisos necesarios.
    8.3 Es responsable del cumplimiento de medidas de seguridad.
    8.4 La plataforma no se responsabiliza por incumplimientos legales del organizador.
    
    9. CONSECUENCIAS DEL INCUMPLIMIENTO
    9.1 El incumplimiento puede resultar en la suspensión del evento.
    9.2 Puede afectar la capacidad de publicar eventos futuros.
    9.3 Aplican penalidades según la gravedad del incumplimiento.
    9.4 La plataforma se reserva acciones legales en casos graves.
    
    10. ACEPTACIÓN FINAL
    Al aceptar estos términos y publicar el evento, confirma que comprende todas las responsabilidades y compromisos adquiridos. La publicación constituye un acuerdo vinculante entre usted y la plataforma.
    
    IMPORTANTE: Una vez publicado, el evento estará disponible públicamente y se habilitará la venta de tickets. Asegúrese de que toda la información esté correcta antes de proceder.
  `;

  // Función para validar el evento
  const validateEvento = (evento) => {
    let checks = [];

    // 1. Validar acceptCreation
    checks.push({
      name: "Confirmación de creación",
      passed: evento.acceptCreation === 1,
      message:
        evento.acceptCreation === 1
          ? "El evento ha confirmado su creación"
          : "El evento debe confirmar su creación antes de publicar",
    });

    // 2. Validar descripción
    checks.push({
      name: "Descripción del evento",
      passed: evento.descripcion !== null && evento.descripcion !== "",
      message:
        evento.descripcion !== null && evento.descripcion !== ""
          ? "El evento tiene descripción"
          : "El evento debe tener una descripción",
    });

    // 3. Validar imagen
    checks.push({
      name: "Imagen del evento",
      passed: evento.imgEvento !== null && evento.imgEvento !== "",
      message:
        evento.imgEvento !== null && evento.imgEvento !== ""
          ? "El evento tiene una imagen"
          : "El evento debe tener una imagen",
    });

    // 4. Validar dirección
    checks.push({
      name: "Dirección del evento",
      passed: evento.direccion !== null && evento.direccion !== "",
      message:
        evento.direccion !== null && evento.direccion !== ""
          ? "El evento tiene dirección"
          : "El evento debe tener una dirección",
    });

    // 5. Validar categoría del evento
    checks.push({
      name: "Categoría del evento",
      passed: evento.categoriaEvento !== null && evento.categoriaEvento !== "",
      message:
        evento.categoriaEvento !== null && evento.categoriaEvento !== ""
          ? `El evento tiene la categoría "${evento.categoriaEvento}" asignada`
          : "El evento debe tener una categoría asignada",
    });

    // 6. Validar ubicación (Google Maps)
    const hasLocation = evento.ubicacion !== null && evento.ubicacion !== "";
    const isValidLocation =
      !hasLocation || evento.ubicacion.startsWith("https://maps.app.goo.gl");
    checks.push({
      name: "Ubicación en Google Maps",
      passed: isValidLocation,
      message: isValidLocation
        ? hasLocation
          ? "El evento tiene un enlace válido de Google Maps"
          : "El evento no tiene ubicación configurada (opcional)"
        : "Si se proporciona ubicación, debe ser un enlace válido de Google Maps (https://maps.app.goo.gl)",
    });

    // 7. Validar fecha de inicio
    const hasStartDate =
      evento.fechaHoraInicio !== null && evento.fechaHoraInicio !== "";
    checks.push({
      name: "Fecha de inicio",
      passed: hasStartDate,
      message: hasStartDate
        ? "El evento tiene fecha de inicio"
        : "El evento debe tener fecha de inicio",
    });
    // 7.1 Validar que la fecha de inicio no sea anterior a hoy
    if (hasStartDate) {
      const now = new Date();
      const startDate = new Date(evento.fechaHoraInicio);
      const isStartDateValid = startDate > now;
      checks.push({
        name: "Validación de fecha de inicio",
        passed: isStartDateValid,
        message: isStartDateValid
          ? "La fecha de inicio del evento es válida"
          : "La fecha de inicio del evento ya ha pasado. No se puede publicar un evento que ya comenzó",
      });
    }

    // 8. Validar fecha de fin
    const hasEndDate =
      evento.fechaHoraFin !== null && evento.fechaHoraFin !== "";
    checks.push({
      name: "Fecha de fin",
      passed: hasEndDate,
      message: hasEndDate
        ? "El evento tiene fecha de fin"
        : "El evento debe tener fecha de fin",
    });

    // 8.1 Validar que la fecha de fin no sea anterior a hoy
    if (hasEndDate) {
      const now = new Date();
      const endDate = new Date(evento.fechaHoraFin);
      const isEndDateValid = endDate > now;

      checks.push({
        name: "Validación de fecha de fin",
        passed: isEndDateValid,
        message: isEndDateValid
          ? "La fecha de fin del evento es válida"
          : "La fecha de fin del evento ya ha pasado. No se puede publicar un evento finalizado",
      });
    }

    // 9. Validar lógica de fechas
    if (hasStartDate && hasEndDate) {
      const startDate = new Date(evento.fechaHoraInicio);
      const endDate = new Date(evento.fechaHoraFin);
      const isLogicalDates = endDate >= startDate;

      checks.push({
        name: "Validación de fechas",
        passed: isLogicalDates,
        message: isLogicalDates
          ? "Las fechas del evento son lógicas"
          : "La fecha de fin no puede ser anterior a la fecha de inicio",
      });
    }

    // 10. Validar promoción
    if (evento.enPromocion === 1) {
      const hasPromoText =
        evento.textoPromocion !== null && evento.textoPromocion !== "";
      checks.push({
        name: "Texto de promoción",
        passed: hasPromoText,
        message: hasPromoText
          ? "El evento en promoción tiene texto descriptivo"
          : "El evento en promoción debe tener texto descriptivo",
      });
    }

    // 11. Validar embajadores
    if (evento.useAmbassador === 1) {
      const hasIncentive = evento.ambassadorIncentive > 0;
      checks.push({
        name: "Incentivo de embajadores",
        passed: hasIncentive,
        message: hasIncentive
          ? `El evento tiene un incentivo de ${evento.ambassadorIncentive}% para embajadores`
          : "El evento debe tener un incentivo mayor a 0% para embajadores",
      });
    }

    // 12. Validar tipos de ticket
    const hasTickets = evento.tiposTicket && evento.tiposTicket.length > 0;
    checks.push({
      name: "Tipos de ticket",
      passed: hasTickets,
      message: hasTickets
        ? `El evento tiene ${evento.tiposTicket.length} tipo(s) de ticket configurado(s)`
        : "El evento debe tener al menos un tipo de ticket",
    });

    // 13-N. Validar cada tipo de ticket
    if (hasTickets) {
      evento.tiposTicket.forEach((ticket, index) => {
        const ticketName = ticket.customTypeName || `Ticket ${index + 1}`;

        // Validar nombre del ticket
        checks.push({
          name: `Nombre del ${ticketName}`,
          passed:
            ticket.customTypeName !== null && ticket.customTypeName !== "",
          message:
            ticket.customTypeName !== null && ticket.customTypeName !== ""
              ? `El ${ticketName} tiene nombre`
              : `El ticket #${index + 1} debe tener un nombre`,
        });

        // Validar precio
        const hasValidPrice = ticket.precio !== null && ticket.precio >= 3500;
        checks.push({
          name: `Precio del tipo de ticket "${ticketName}"`,
          passed: hasValidPrice,
          message: hasValidPrice
            ? `El ${ticketName} tiene un precio válido (CLP $${ticket.precio?.toLocaleString(
                "es-CL",
              )})`
            : `El tipo de ticket "${ticketName}" debe tener un precio mínimo de CLP $3,500`,
        });

        // Validar precio sin IVA
        // checks.push({
        //   name: `Precio sin IVA del tipo de ticket "${ticketName}"`,
        //   passed: ticket.precioSinIva !== null,
        //   message:
        //     ticket.precioSinIva !== null
        //       ? `El tipo de ticket "${ticketName}" tiene precio sin IVA configurado`
        //       : `El tipo de ticket "${ticketName}" debe tener precio sin IVA`,
        // });

        // Validar utilidad neta
        // checks.push({
        //   name: `Utilidad neta del tipo de ticket "${ticketName}"`,
        //   passed:
        //     ticket.utilidadNeta !== null && ticket.utilidadNetaConIva !== null,
        //   message:
        //     ticket.utilidadNeta !== null && ticket.utilidadNetaConIva !== null
        //       ? `El tipo de ticket "${ticketName}" tiene utilidad neta configurada`
        //       : `El tipo de ticket "${ticketName}" debe tener utilidad neta configurada`,
        // });

        // Validar comisión AppWhoop
        // checks.push({
        //   name: `Comisión AppWhoop del tipo de ticket "${ticketName}"`,
        //   passed:
        //     ticket.comisionAW !== null && ticket.comisionAWConIva !== null,
        //   message:
        //     ticket.comisionAW !== null && ticket.comisionAWConIva !== null
        //       ? `El tipo de ticket "${ticketName}" tiene comisión AppWhoop configurada`
        //       : `El tipo de ticket "${ticketName}" debe tener comisión AppWhoop configurada`,
        // });

        // Validar comisión de embajador si aplica
        // if (evento.useAmbassador === 1) {
        //   const hasAmbassadorCommission =
        //     ticket.comisionAmbassador !== null &&
        //     ticket.comisionAmbassadorConIva !== null;
        //   checks.push({
        //     name: `Comisión de embajador del tipo de ticket "${ticketName}"`,
        //     passed: hasAmbassadorCommission,
        //     message: hasAmbassadorCommission
        //       ? `El tipo de ticket "${ticketName}" tiene comisión de embajador configurada`
        //       : `El tipo de ticket "${ticketName}" debe tener comisión de embajador configurada`,
        //   });
        // }

        // Validar límite por persona
        checks.push({
          name: `Límite por persona del tipo de ticket "${ticketName}"`,
          passed:
            ticket.limitePorPersona !== null && ticket.limitePorPersona > 0,
          message:
            ticket.limitePorPersona !== null && ticket.limitePorPersona > 0
              ? `El tipo de ticket "${ticketName}" tiene límite de ${ticket.limitePorPersona} por persona`
              : `El tipo de ticket "${ticketName}" debe tener un límite por persona`,
        });

        // Validar ticket grupal
        if (ticket.ticketGrupal === 1) {
          const hasValidGroupSize =
            ticket.cantidadPersonasTicketGrupal !== null &&
            ticket.cantidadPersonasTicketGrupal > 1;
          checks.push({
            name: `Tamaño de grupo del tipo de ticket "${ticketName}"`,
            passed: hasValidGroupSize,
            message: hasValidGroupSize
              ? `El tipo de ticket "${ticketName}" es para ${ticket.cantidadPersonasTicketGrupal} personas`
              : `El tipo de ticket "${ticketName}" grupal debe tener cantidad de personas mayor a 1`,
          });
        }

        // Validar imagen del ticket
        if (ticket.useImgEvento === 0) {
          const hasTicketImage =
            ticket.imgTicket !== null && ticket.imgTicket !== "";
          checks.push({
            name: `Imagen del tipo de ticket "${ticketName}"`,
            passed: hasTicketImage,
            message: hasTicketImage
              ? `El tipo de ticket "${ticketName}" tiene imagen personalizada`
              : `El tipo de ticket "${ticketName}" debe tener una imagen personalizada`,
          });
        }

        // Validaciones de campos de invitación (solo si esInvitacion está activo)
        if (ticket.esInvitacion) {
          // Regla 1: cupo gratuito no puede ser mayor a la cantidad en venta
          if (ticket.invitacionCupoGratuito !== null && ticket.invitacionCupoGratuito !== undefined) {
            const cupoValido = ticket.invitacionCupoGratuito <= (ticket.cantidadEnVenta || 0);
            checks.push({
              name: `Cupo gratuito del tipo de ticket "${ticketName}"`,
              passed: cupoValido,
              message: cupoValido
                ? `El cupo gratuito de "${ticketName}" es válido (${ticket.invitacionCupoGratuito} de ${ticket.cantidadEnVenta})`
                : `El cupo gratuito de "${ticketName}" (${ticket.invitacionCupoGratuito}) no puede ser mayor a la cantidad de tickets en venta (${ticket.cantidadEnVenta})`,
            });
          }

          // Regla 2: descuento fijo debe ser menor al precio base
          if (ticket.invitacionDescuento !== null && ticket.invitacionDescuento !== undefined) {
            const descuentoValido = ticket.precio > 0 && ticket.invitacionDescuento < ticket.precio;
            checks.push({
              name: `Descuento fijo del tipo de ticket "${ticketName}"`,
              passed: descuentoValido,
              message: descuentoValido
                ? `El descuento fijo de "${ticketName}" es válido (CLP $${ticket.invitacionDescuento?.toLocaleString("es-CL")} de CLP $${ticket.precio?.toLocaleString("es-CL")})`
                : !ticket.precio
                  ? `El tipo de ticket "${ticketName}" tiene descuento fijo pero no tiene precio definido`
                  : `El descuento fijo de "${ticketName}" (CLP $${ticket.invitacionDescuento?.toLocaleString("es-CL")}) debe ser menor al precio base (CLP $${ticket.precio?.toLocaleString("es-CL")})`,
            });
          }

          // Regla 3: hora límite entrada gratuita no puede ser anterior al inicio del evento
          if (ticket.invitacionHoraLimiteGratuita) {
            const horaLimiteGratuita = new Date(ticket.invitacionHoraLimiteGratuita);
            const fechaInicioEvento = new Date(evento.fechaHoraInicio);
            const horaGratuitaValida = horaLimiteGratuita >= fechaInicioEvento;
            checks.push({
              name: `Hora límite entrada gratuita de "${ticketName}"`,
              passed: horaGratuitaValida,
              message: horaGratuitaValida
                ? `La hora límite de entrada gratuita de "${ticketName}" es válida`
                : `La hora límite de entrada gratuita de "${ticketName}" no puede ser anterior al inicio del evento`,
            });
          }

          // Regla 4: hora límite descuento no puede ser anterior a hora límite entrada gratuita (solo si ambas existen)
          if (ticket.invitacionHoraLimiteDescuento && ticket.invitacionHoraLimiteGratuita) {
            const horaLimiteDescuento = new Date(ticket.invitacionHoraLimiteDescuento);
            const horaLimiteGratuita = new Date(ticket.invitacionHoraLimiteGratuita);
            const horaDescuentoValida = horaLimiteDescuento >= horaLimiteGratuita;
            checks.push({
              name: `Hora límite descuento de "${ticketName}"`,
              passed: horaDescuentoValida,
              message: horaDescuentoValida
                ? `La hora límite de descuento de "${ticketName}" es válida`
                : `La hora límite de descuento de "${ticketName}" no puede ser anterior a la hora límite de entrada gratuita`,
            });
          }

          // Regla 5: si hay hora límite descuento pero NO hay hora límite gratuita, tampoco puede ser anterior al inicio del evento
          if (ticket.invitacionHoraLimiteDescuento && !ticket.invitacionHoraLimiteGratuita) {
            const horaLimiteDescuento = new Date(ticket.invitacionHoraLimiteDescuento);
            const fechaInicioEvento = new Date(evento.fechaHoraInicio);
            const horaDescuentoVsInicioValida = horaLimiteDescuento >= fechaInicioEvento;
            checks.push({
              name: `Hora límite descuento (vs inicio evento) de "${ticketName}"`,
              passed: horaDescuentoVsInicioValida,
              message: horaDescuentoVsInicioValida
                ? `La hora límite de descuento de "${ticketName}" es válida`
                : `La hora límite de descuento de "${ticketName}" no puede ser anterior al inicio del evento`,
            });
          }
        }
      });

      // Validar aforo
      let totalTicketsEnVenta = 0;
      evento.tiposTicket.forEach((ticket) => {
        const cantidad = ticket.cantidadEnVenta || 0;
        const multiplicador =
          ticket.ticketGrupal === 1
            ? ticket.cantidadPersonasTicketGrupal || 1
            : 1;
        totalTicketsEnVenta += cantidad * multiplicador;
      });

      const aforo = evento.aforo || 0;
      const aforoExceeded = totalTicketsEnVenta > aforo;
      const aforoPercentage =
        aforo > 0 ? (totalTicketsEnVenta / aforo) * 100 : 0;

      // Determinar estado del aforo
      let aforoStatusValue = "low";
      if (aforoExceeded) {
        aforoStatusValue = "exceeded";
      } else if (aforoPercentage >= 50) {
        aforoStatusValue = "good";
      } else if (aforoPercentage >= 25) {
        aforoStatusValue = "moderate";
      }

      setAforoStatus({
        status: aforoStatusValue,
        percentage: aforoPercentage.toFixed(1),
        total: totalTicketsEnVenta,
        aforo: aforo,
      });

      checks.push({
        name: "Validación de aforo",
        passed: !aforoExceeded,
        message: aforoExceeded
          ? `El aforo ha sido excedido: ${totalTicketsEnVenta} tickets vs ${aforo} aforo máximo`
          : `Aforo validado: ${totalTicketsEnVenta} de ${aforo} tickets (${aforoPercentage.toFixed(
              1,
            )}%)`,
      });
    }

    // Validar el nivel de configuración de uso de embajadores
    switch (evento.useAmbassador) {
      case 0:
        checks.push({
          name: "Uso de embajadores",
          passed: true,
          severity: "warning",
          message:
            "El evento no tiene habilitado el uso de embajadores. Se recomienda activar esta opción para maximizar la promoción y ventas del evento.",
        });
        break;
      case 1:
        checks.push({
          name: "Uso de embajadores",
          passed: true,
          severity: "warning",
          message:
            "El evento tiene habilitado el uso de embajadores, pero aún no usas todo el potencial de esta funcionalidad. Puedes publicar y luego actualizar la configuración si así lo deseas.",
        });
        break;
      case 2:
        checks.push({
          name: "Uso de embajadores",
          passed: true,
          message:
            "El evento tiene habilitado el uso completo de embajadores y está aprovechando todas las funcionalidades disponibles para maximizar la promoción y ventas.",
        });
        if (evento.totalEmbajadores && evento.totalEmbajadores > 0) {
          checks.push({
            name: "Embajadores activos",
            passed: true,
            message: `El evento cuenta con ${evento.totalEmbajadores} embajador(es) activo(s) para promover el evento.`,
          });
        } else {
          checks.push({
            name: "Embajadores activos",
            passed: true,
            severity: "warning",
            message:
              "El evento no cuenta con embajadores activos. Se recomienda seleccionar embajadores para maximizar la promoción y ventas del evento.",
          });
        }
        break;
      default:
        checks.push({
          name: "Uso de embajadores",
          passed: false,
          message:
            "El nivel de uso de embajadores del evento es desconocido. Se recomienda revisar la configuración para maximizar los beneficios.",
        });
    }

    return checks;
  };

  // Ejecutar validaciones al cargar el componente
  useEffect(() => {
    let isMounted = true; // Flag para evitar actualizaciones en componente desmontado

    const runValidations = async () => {
      const uidEvento = eventoUid || paramFromUrl;

      if (!uidEvento) {
        console.error("No se encontró UID del evento");
        return;
      }

      // Siempre pedir datos frescos al montar por primera vez
      if (!hasFetchedFreshRef.current) {
        hasFetchedFreshRef.current = true;
        dispatch(getEventoInfo({ uidEvento }));
        return; // El efecto se re-ejecutará cuando infoEvento cambie
      }

      // Esperar a que infoEvento tenga los datos del evento correcto
      if (!infoEvento || infoEvento.uid !== uidEvento) {
        return;
      }

      // Solo continuar si el componente sigue montado
      if (!isMounted) return;

      // Limpiar estados antes de validar
      setValidations([]);
      setIsValidating(true);
      setAllValidationsPassed(false);
      setAcceptTerms(false);
      setAforoStatus(null);

      const checks = validateEvento(infoEvento);
      console.log("Validation checks:", checks.length, "validations");

      // Mostrar validaciones una por una con delay
      for (let i = 0; i < checks.length; i++) {
        if (!isMounted) break; // Detener si el componente se desmontó

        await new Promise((resolve) => setTimeout(resolve, 150));

        if (isMounted) {
          setValidations((prev) => [...prev, checks[i]]);
        }
      }

      if (isMounted) {
        setIsValidating(false);

        // Verificar si todas pasaron (ignorar warnings que son opcionales)
        const allPassed = checks.every(
          (check) => check.severity === "warning" || check.passed
        );
        setAllValidationsPassed(allPassed);
      }
    };

    runValidations();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [eventoUid, paramFromUrl, infoEvento, dispatch]);

  console.log("Validations:", validations);
  // Manejar scroll en términos y condiciones
  const handleTermsScroll = () => {
    if (termContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        termContainerRef.current;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      // Marcar como leído cuando se llega al 95% del contenido
      if (scrollPercentage >= 0.95) {
        setHasReadTerms(true);
      }
    }
  };

  // Manejar cambios en el checkbox
  const handleAcceptChange = (e) => {
    setAcceptTerms(e.target.checked);

    // Limpiar errores al cambiar
    if (formErrors.acceptTerms) {
      setFormErrors((prev) => ({ ...prev, acceptTerms: null }));
    }
  };

  // Validar formulario
  const validateForm = () => {
    const errors = {};

    // if (!hasReadTerms) {
    //   errors.terms = "Debe leer completamente los términos y condiciones";
    // }

    console.log("Validating form: acceptTerms =", acceptTerms);
    if (!acceptTerms) {
      errors.acceptTerms =
        "Debe aceptar los términos y condiciones para publicar el evento";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    acceptTerms && console.log("Terms accepted", validateForm());
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Usar eventoUid prop, uid del evento desde Redux, o parámetro de URL como respaldo
      const uidEvento = eventoUid || infoEvento?.uid || paramFromUrl;

      if (!uidEvento) {
        setFormErrors({
          general: "No se encontró el UID del evento para publicar",
        });
        return;
      }

      const result = await dispatch(
        publicarEvento({
          uidEvento,
        }),
      );

      if (publicarEvento.fulfilled.match(result)) {
        // Éxito - mostrar mensaje de éxito
        setPublishSuccess(true);
        console.log("¡Evento publicado exitosamente!");

        // Cerrar modal después de un breve delay para mostrar el mensaje
        setTimeout(() => {
          handleClose();
          // Opcional: refrescar los datos del evento
          // dispatch(getEventoInfo({ uidEvento }));
          if (idEmpresa != null) {
            // Navegar después de publicar exitosamente
            setTimeout(() => {
              navigate(`/empresa/${idEmpresa}`);
            }, 1500);
          } else {
            window.location.reload();
          }
        }, 2000);
      } else {
        // Error en la publicación
        console.error("Error al publicar evento:", result.payload);
        setFormErrors({
          general:
            result.payload ||
            "Error al publicar el evento. Intente nuevamente.",
        });
      }
    } catch (error) {
      console.error("Error inesperado al publicar evento:", error);
      setFormErrors({
        general: "Error inesperado. Por favor intente nuevamente.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Limpiar errores al cerrar
  const handleClose = () => {
    setFormErrors({});
    setPublishSuccess(false);
    setAcceptTerms(false);
    onClose();
  };

  // Verificar si el formulario es válido
  const isFormValid = allValidationsPassed && acceptTerms;

  // Renderizar el color del aforo
  const getAforoColor = () => {
    if (!aforoStatus) return "";
    switch (aforoStatus.status) {
      case "good":
        return "bg-green-50 border-green-500 text-green-700";
      case "moderate":
        return "bg-yellow-50 border-yellow-500 text-yellow-700";
      case "low":
        return "bg-purple-50 border-purple-500 text-purple-700";
      case "exceeded":
        return "bg-red-50 border-red-500 text-red-700";
      default:
        return "";
    }
  };

  const getAforoIcon = () => {
    if (!aforoStatus) return "fa-info-circle";
    switch (aforoStatus.status) {
      case "good":
        return "fa-check-circle";
      case "moderate":
        return "fa-exclamation-triangle";
      case "low":
        return "fa-exclamation-circle";
      case "exceeded":
        return "fa-times-circle";
      default:
        return "fa-info-circle";
    }
  };

  const getAforoMessage = () => {
    if (!aforoStatus) return "";
    switch (aforoStatus.status) {
      case "good":
        return `Excelente: Estás vendiendo el ${aforoStatus.percentage}% del aforo total`;
      case "moderate":
        return `Aceptable: Estás vendiendo el ${aforoStatus.percentage}% del aforo total`;
      case "low":
        return `Bajo: Solo estás vendiendo el ${aforoStatus.percentage}% del aforo total. Considera aumentar la cantidad de tickets en venta`;
      case "exceeded":
        return `Error: Los tickets en venta (${aforoStatus.total}) exceden el aforo máximo (${aforoStatus.aforo})`;
      default:
        return "";
    }
  };

  return (
    <>
      {/* Modal de Términos y Condiciones */}
      <Modal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        large="max-w-3xl"
      >
        <div className="bg-aw-bgbluedark rounded-2xl p-6 inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green">
          <h2 className="text-2xl font-bold text-white mb-4">
            Términos y Condiciones
          </h2>
          <div className="max-h-[70vh] overflow-y-auto scrollbar-box thin">
            <div className="whitespace-pre-wrap text-sm text-gray-300">
              {termsAndConditions}
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <button
              onClick={() => setShowTermsModal(false)}
              className="px-6 py-2 bg-aw-green text-aw-bluedark rounded-md font-medium hover:bg-aw-green/90 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </Modal>

      {/* Formulario Principal */}
      <div className="mx-auto p-6 bg-aw-bgbluedark rounded-lg inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green max-w-2xl scrollbar-box thin h-[85dvh] overflow-y-auto">
        {publishSuccess ? (
          // Mensaje de éxito
          <div className="h-full flex flex-col items-center justify-center text-center py-8">
            <div className="mb-6">
              <i className="fas fa-check-circle text-6xl text-green-500 mb-4"></i>
              <h2 className="text-2xl font-bold text-white mb-2">
                ¡Evento Publicado!
              </h2>
              <p className="text-gray-300">
                Su evento ha sido publicado exitosamente y ya está disponible
                para el público.
              </p>
            </div>
            <div className="flex justify-center">
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-aw-green text-aw-bluedark rounded-md font-medium hover:bg-aw-green/90 transition-colors"
              >
                Continuar
              </button>
            </div>
          </div>
        ) : (
          // Formulario de validación
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white text-center mb-2">
                Publicar Evento
              </h2>
              <p className="text-white text-sm">
                Validando la información del evento antes de publicar...
              </p>
            </div>

            <form className="space-y-6">
              {/* Lista de Validaciones */}
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-white mb-3">
                  <i className="fas fa-clipboard-check mr-2 text-aw-green"></i>
                  Verificación de Requisitos
                </h3>

                <AnimatePresence>
                  {validations.map((validation, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3 }}
                      className={`mb-1 rounded-lg`}
                    >
                      <div className="flex items-start gap-3">
                        <FontAwesomeIcon
                          icon={`fa-solid ${
                            validation.severity === "warning"
                              ? "fa-exclamation-circle"
                              : validation.passed
                              ? "fa-check-circle"
                              : "fa-exclamation-triangle"
                          }`}
                          className={`${
                            validation.severity === "warning"
                              ? "text-yellow-500"
                              : validation.passed
                              ? "text-aw-green"
                              : "text-red-500"
                          } mt-0.5`}
                        />
                        <div className="flex-1">
                          <p
                            className={`text-sm font-semibold ${
                              validation.severity === "warning"
                                ? "text-yellow-500"
                                : validation.passed
                                ? "text-aw-green"
                                : "text-red-500"
                            }`}
                          >
                            {validation.name}
                            <span
                              className={`text-xs font-medium inline mt-1 ml-1 ${
                                validation.severity === "warning"
                                  ? "text-yellow-400/80"
                                  : validation.passed
                                  ? "text-aw-green/60"
                                  : "text-red-300/60"
                              }`}
                            >
                              - {validation.message}
                            </span>
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Indicador de carga */}
                {isValidating && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center justify-center py-4"
                  >
                    <i className="fas fa-spinner fa-spin text-aw-green text-2xl mr-3"></i>
                    <span className="text-gray-300">
                      Validando información...
                    </span>
                  </motion.div>
                )}
              </div>

              {/* Advertencia de Aforo */}
              {!isValidating &&
                aforoStatus &&
                aforoStatus.status !== "exceeded" && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 rounded-lg border-2 ${getAforoColor()}`}
                  >
                    <div className="flex items-start gap-3">
                      <i className={`fas ${getAforoIcon()} text-xl mt-0.5`}></i>
                      <div className="flex-1">
                        <h4 className="font-semibold mb-1">Estado del Aforo</h4>
                        <p className="text-sm">{getAforoMessage()}</p>
                        <div className="mt-2 text-xs">
                          <strong>Tickets en venta:</strong> {aforoStatus.total}{" "}
                          | <strong>Aforo total:</strong> {aforoStatus.aforo}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

              {/* Resumen de Advertencias */}
              {/* {!isValidating &&
                validations.filter((v) => v.severity === "warning").length >
                  0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-yellow-50/10 border-2 border-yellow-500/50 rounded-lg"
                  >
                    <div className="flex items-start gap-3">
                      <FontAwesomeIcon
                        icon="fa-solid fa-exclamation-circle"
                        className="text-yellow-500 text-xl mt-0.5"
                      />
                      <div className="flex-1">
                        <h4 className="font-semibold text-yellow-500 mb-2">
                          Recomendaciones para optimizar tu evento
                        </h4>
                        <p className="text-sm text-yellow-400/90 mb-2">
                          Se encontraron{" "}
                          {validations.filter((v) => v.severity === "warning").length}{" "}
                          recomendación(es) opcional(es) para mejorar tu evento:
                        </p>
                        <ul className="text-xs text-yellow-300/80 space-y-1 ml-4 list-disc">
                          {validations
                            .filter((v) => v.severity === "warning")
                            .map((v, i) => (
                              <li key={i}>
                                <strong>{v.name}:</strong> {v.message}
                              </li>
                            ))}
                        </ul>
                        <p className="text-sm text-yellow-400/90 mt-3 font-medium">
                          Estas son recomendaciones opcionales. Puedes publicar el
                          evento de todas formas.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )} */}

              {/* Resumen de Errores */}
              {!isValidating && !allValidationsPassed && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-red-700/5 border-2 border-red-300 rounded-lg"
                >
                  <div className="flex items-start gap-3">
                    <FontAwesomeIcon
                      icon="fa-solid fa-times-circle"
                      className="text-red-500 text-xl mt-0.5"
                    />
                    <div className="flex-1">
                      <h4 className="font-semibold text-red-500 mb-2">
                        No se puede publicar el evento
                      </h4>
                      <p className="text-sm text-red-400 mb-2">
                        Se encontraron{" "}
                        {validations.filter((v) => !v.passed && v.severity !== "warning").length}{" "}
                        problema(s) que deben ser corregidos:
                      </p>
                      <ul className="text-xs text-red-200 space-y-1 ml-4 list-disc">
                        {validations
                          .filter((v) => !v.passed && v.severity !== "warning")
                          .map((v, i) => (
                            <li key={i}>{v.name}</li>
                          ))}
                      </ul>
                      <p className="text-sm text-red-400 mt-3 font-medium">
                        Por favor, corrija estos problemas antes de intentar
                        publicar el evento.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Error del servidor */}
              {(errorEvento || formErrors.general) && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">
                    {formErrors.general ||
                      (typeof errorEvento === "string"
                        ? errorEvento
                        : errorEvento?.message ||
                          "Error al publicar el evento")}
                  </p>
                </div>
              )}

              {/* Advertencia importante */}
              {!isValidating && allValidationsPassed && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="p-2 bg-yellow-50 border border-yellow-200 rounded-md"
                >
                  <div className="flex">
                    <i className="fas fa-exclamation-triangle text-yellow-400 mt-0.5"></i>
                    <div className="ml-3">
                      <h3 className="text-sm font-semibold text-yellow-800">
                        Importante
                      </h3>
                      <p className="text-xs text-yellow-700 mt-1">
                        Una vez publicado, el evento estará disponible
                        públicamente y los usuarios podrán comprar tickets.
                        Asegúrese de que toda la información del evento esté
                        correcta y completa.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Checkbox de Aceptación de Términos */}
              {!isValidating && allValidationsPassed && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="p-4 bg-aw-bluedark rounded-lg border border-gray-700"
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="acceptTerms"
                      name="acceptTerms"
                      checked={acceptTerms}
                      onChange={handleAcceptChange}
                      className="mt-1 h-4 w-4 text-aw-green border-gray-300 rounded focus:ring-aw-green/75"
                    />
                    <label
                      htmlFor="acceptTerms"
                      className="text-sm text-white flex-1"
                    >
                      Acepto los{" "}
                      <button
                        type="button"
                        onClick={() => setShowTermsModal(true)}
                        className="text-aw-green hover:text-aw-green/80 underline font-medium"
                      >
                        términos y condiciones
                      </button>{" "}
                      para publicar el evento
                    </label>
                  </div>
                  {formErrors.acceptTerms && (
                    <p className="mt-2 text-sm text-red-400 ml-7">
                      {formErrors.acceptTerms}
                    </p>
                  )}
                </motion.div>
              )}

              {/* Botones de acción */}
              <div className="flex justify-end space-x-4 pt-6">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-6 py-2 border border-gray-300 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-aw-green/75 duration-300 hover:scale-95"
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>

                {!isValidating && allValidationsPassed && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={!isFormValid || isSubmitting}
                      className={`px-6 py-2 rounded-md text-aw-bluedark focus:outline-none focus:ring-2 focus:ring-aw-green/75 flex items-center space-x-2 duration-300 ${
                        !isFormValid || isSubmitting
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-aw-green hover:scale-95"
                      }`}
                    >
                      {isSubmitting && (
                        <i className="fas fa-spinner animate-spin"></i>
                      )}
                      <span>
                        {isSubmitting ? "Publicando..." : "Publicar Evento"}
                      </span>
                    </button>
                  </motion.div>
                )}
              </div>
            </form>
          </>
        )}
      </div>
    </>
  );
}
